import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import {
  AlertService,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import {
  PaginatedPreferredTreadSpecificationResponse,
  Pagination,
  PreferredTreadsFiltersResponse,
  PreferredTreadSpecification
} from '../../models/response.model';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { CustomerAssignService } from '../../../../shared/services/customer-assign.service';
import { ProductDataService } from '../../services/product-data.service';

@Component({
  selector: 'app-preferred-treads',
  templateUrl: './preferred-treads.component.html',
  styleUrl: './preferred-treads.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreferredTreadsComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public customerAssignService = inject(CustomerAssignService);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  saveEnabled = false;
  productSaveDisabled = true;
  preferredTreadDescriptionList: string[] = [];
  preferredTreadCodeList: string[] = [];
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  preferredTreadSpecificationList: PreferredTreadSpecification[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  currentPreferredTread: PreferredTreadSpecification;
  deleteableTreads: string[];
  locallyAddedRowsList: PreferredTreadSpecification[] = [];
  itemToDelete: PreferredTreadSpecification;
  preferredTreadsBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { label: this.translationService.translate('productDataText') },
    { link: '/home/productData/preferred-treads', label: this.translationService.translate('preferredTreadsText') }
  ];
  additionalColumns = [
    {
      property: 'preferredTreadCode',
      headerKey: this.translationService.translate('preferredTreadsText') + this.translationService.translate('codeText')
    },
    {
      property: 'preferredTreadDesc',
      headerKey: this.translationService.translate('preferredTreadsText') + this.translationService.translate('descText')
    }
  ];

  tableConfig: TableConfig<PreferredTreadSpecification> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {},
    onRowClick: (elm: PreferredTreadSpecification) => {
      this.router.navigateByUrl(`/home/productData/preferred-treads/preferred-treads-setup/${elm.preferredTreadSpecificationPk}`, {
        state: { code: elm.preferredTreadCode, description: elm.preferredTreadDescription }
      });
    }
  };
  state = TableConfig.getState(this.tableConfig, this.route);

  fetcher: PaginatorFetcher<PreferredTreadSpecification> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getPreferredTreads({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((preferredTreadResponse: PaginatedPreferredTreadSpecificationResponse) => {
        this.preferredTreadSpecificationList = preferredTreadResponse.data.preferredTreadSpecifications || [];
        this.getDeletableTreads(this.preferredTreadSpecificationList);
        this.pagination = preferredTreadResponse.data.pagination;
        this.updateDisplayedRowsForm([...this.locallyAddedRowsList, ...this.preferredTreadSpecificationList]);
        this.cdr.markForCheck();
        const count = [...this.locallyAddedRowsList, ...this.preferredTreadSpecificationList].length;
        return {
          items: [...this.locallyAddedRowsList, ...this.preferredTreadSpecificationList],
          count
        };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      codes: [''],
      descriptions: ['']
    });

    this.filterFormBackend = this.fb.group({
      codes: [''],
      descriptions: ['']
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    this.setupFormSubscriptions();
    this.productDataService.getPreferredTreadsFilters().subscribe((response: PreferredTreadsFiltersResponse) => {
      this.preferredTreadCodeList = response.data.preferredTreadsCodes;
      this.preferredTreadDescriptionList = response.data.preferredTreadsDescriptions;
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: {
          cell: (row) => row.preferredTreadCode,
          header: this.translationService.translate('codeText')
        },
        description: {
          cell: (row) => row.preferredTreadDescription,
          header: this.translationService.translate('descriptionText')
        }
      };
      this.cdr.markForCheck();
    });
  }

  public updateDisplayedRowsForm(rows: PreferredTreadSpecification[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: PreferredTreadSpecification): FormGroup {
    return this.fb.group({
      code: [row.preferredTreadCode],
      description: [row.preferredTreadDescription]
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.filterFormBackend.reset();
    this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
  }

  setupFormSubscriptions(): void {
    this.filterForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  generateFilterForm(): void {
    const formValue = this.filterForm.value;
    this.filterFormBackend.patchValue({
      codes: formValue.codes,
      descriptions: formValue.descriptions
    });
  }

  createTread(): void {
    this.router.navigateByUrl(`/home/productData/preferred-treads/preferred-treads-setup/`, {
      state: { code: '', description: '' }
    });
  }

  getDeletableTreads(preferredTreadSpecs: PreferredTreadSpecification[]): void {
    const payload = { preferredTreadSpecPks: preferredTreadSpecs.map((spec) => spec.preferredTreadSpecificationPk) };
    this.productDataService.canPreferredTreadSpecBeDeleted(payload).subscribe((response: any) => {
      this.deleteableTreads = Array.isArray(response?.data) ? response.data : [];
    });
  }

  protected isDeletable(item: PreferredTreadSpecification): boolean {
    return Array.isArray(this.deleteableTreads) && !this.deleteableTreads.includes(item.preferredTreadSpecificationPk);
  }

  showDeleteModal(item: PreferredTreadSpecification): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  deleteRow(item: PreferredTreadSpecification): void {
    this.productDataService.deletePreferredTreadSpec(item.preferredTreadSpecificationPk).subscribe({
      next: () => {
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    });
  }

  assignToCustomer(item: PreferredTreadSpecification): void {
    this.currentPreferredTread = item;

    const navigationData = {
      breadcrumbLinks: this.preferredTreadsBreadcrumbs,
      currentDataObject: this.currentPreferredTread,
      additionalColumns: this.additionalColumns,
      searchUri: 'searchPreferredTreadSpecificationsAssignToCustomer',
      assignUri: 'assignPreferredTreadSpecificationsToCustomers',
      service: 'backOfficeUrl'
    };

    this.customerAssignService.setNavigationData(navigationData);
    this.router.navigate(['/home/productData/customer-assign', this.currentPreferredTread.preferredTreadSpecificationPk]);
  }

  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}
